import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Smartphone } from "lucide-react";

// Same tenant-subdomain detection as services/api.js's getApiBaseUrl() — kept
// separate (rather than importing API_BASE_URL) because this must reflect
// the ORIGIN the mobile app should connect to, not the current page's API
// base (which can differ in native dev, e.g. :3000 → :8000).
const getTenantConnection = () => {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const reserved = new Set(["www", "admin", "api", "mawered", "traefik", "localhost"]);
  if (parts.length < 3 || reserved.has(parts[0])) return null;
  return {
    subdomain: parts[0],
    baseUrl: `${window.location.protocol}//${hostname}/api`,
  };
};

const CompanyMobileCard = () => {
  const { t } = useTranslation();
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const tenant = getTenantConnection();

  useEffect(() => {
    if (!tenant) return;
    QRCode.toDataURL(JSON.stringify(tenant), { width: 220, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [tenant?.subdomain, tenant?.baseUrl]);

  if (!tenant) return null;

  return (
    <Card>
      <CardHeader className="pb-5">
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(94,106,210,0.10)" }}>
            <Smartphone className="h-4 w-4" style={{ color: "#5e6ad2" }} />
          </div>
          <span>{t("companyConfig.mobile.title")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <div className="shrink-0 p-3 bg-white rounded-xl border" style={{ borderColor: "var(--lm-hairline, #e5e7eb)" }}>
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={t("companyConfig.mobile.qrAlt")} width={180} height={180} />
            ) : (
              <div className="w-[180px] h-[180px]" />
            )}
          </div>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">{t("companyConfig.mobile.description")}</p>
            <p className="font-medium text-foreground">{t("companyConfig.mobile.stepScan")}</p>
            <p className="text-xs text-muted-foreground">{t("companyConfig.mobile.hint")}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyMobileCard;
